import { useLayoutEffect, useRef, useState } from "react";

/**
 * Measures the element's own width, not the viewport's.
 *
 * Matters more here than it looks: the breakdown lives inside the 60% detail
 * column of QuoteDetail, so on a wide screen it still only has ~650px to work
 * with. A viewport breakpoint would report "large" and hand the widest layout
 * to the tightest space. Browser zoom does the same thing — a 1280px screen at
 * 125% behaves like 1024px.
 *
 * useLayoutEffect rather than useEffect so the first measurement happens
 * before paint and a narrow container never flashes the three-column grid.
 */
export function useContainerWidth<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState<number | undefined>(undefined);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const measure = () => setWidth(el.getBoundingClientRect().width);
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, width };
}
